import React, {useContext, useEffect, useState} from "react";
import {
  Backdrop,
  Chip,
  CircularProgress,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  useTheme
} from "@material-ui/core";
import {
  ANNUAL_INCOME_RANGE,
  EMPLOYMENT_STATUS,
  GENDER_FIELD,
  MARTIAL_STATUS,
  PET_OWNERS_STATUS,
  YES_NO_ALL
} from "../constants";
import {WizardContext} from "../context";
import {useStyles} from "../styles";
import useAxios from "axios-hooks";
import {getCustomerLists} from "../../../../Api";
import CustomText from 'Components/Universal/CustomText';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const WizardStep1 = ({showTargetDropdown=true, audienceCount}) => {
  const {formData, fieldChange} = useContext(WizardContext);
  const classes = useStyles();
  const theme = useTheme();
  const [customerLists, setCustomerLists] = useState([]);
  const [{loading, data}] = useAxios(getCustomerLists(), {manual: false});

  useEffect(() => {
    if (data && data.customerList) {
      setCustomerLists(data.customerList);
    }
  }, [data]);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <div style={{ flex: 3 }}>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {showTargetDropdown && (
        <FormControl className={classes.formControl}>
          <InputLabel id="generic-select-field">Select Audience Type</InputLabel>
          <Select
            labelId="generic-select-field"
            id="generic-select"
            value={formData.audienceType}
            onChange={fieldChange('audienceType', false)}
          >
            <MenuItem value='customer_list'>Customer List</MenuItem>
            <MenuItem value='custom'>Custom Audience</MenuItem>
          </Select>
        </FormControl>
      )}
      {formData.audienceType === 'custom' ? (
        <>
          <FormControl className={classes.formControl}>
            <Typography id="range-slider" gutterBottom>
              Age Range ({formData.targeting.ageRange[0]} - {formData.targeting.ageRange[1]})
            </Typography>
            <Slider
              value={formData.targeting.ageRange}
              onChange={(e, value) => fieldChange('ageRange', true)({target: { value }})}
              valueLabelDisplay="auto"
              aria-labelledby="range-slider"
              getAriaValueText={value => value}
              min={18}
              max={100}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="gender-field">Gender</InputLabel>
            <Select
              labelId="gender-field"
              id="gender-field"
              value={formData.targeting.gender}
              onChange={fieldChange('gender', true)}
            >
              {GENDER_FIELD.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-mutiple-chip-label">Relationship Status</InputLabel>
            <Select
              labelId="relationship-field"
              id="relationship-field"
              multiple
              value={formData.targeting.relationship}
              onChange={fieldChange('relationship', true)}
              input={<Input id="select-multiple-chip" />}
              renderValue={selected => (
                <div className={classes.chips}>
                  {selected.map(value => (
                    <Chip key={value} label={value} className={classes.chip} />
                  ))}
                </div>
              )}
              MenuProps={MenuProps}
            >
              {MARTIAL_STATUS.map(name => (
                <MenuItem key={name} value={name} style={getStyles(name, formData.targeting.salary_range, theme)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="relationship-field">Have children?</InputLabel>
            <Select
              labelId="have-children-field"
              id="have-children-field"
              value={formData.targeting.have_children}
              onChange={fieldChange('have_children', true)}
            >
              {YES_NO_ALL.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-mutiple-chip-label">Annual Income</InputLabel>
            <Select
              labelId="annual-income-field"
              id="annual-income-field"
              multiple
              value={formData.targeting.salary_range}
              onChange={fieldChange('salary_range', true)}
              input={<Input id="select-multiple-chip" />}
              renderValue={selected => (
                <div className={classes.chips}>
                  {selected.map(value => (
                    <Chip key={value} label={value} className={classes.chip} />
                  ))}
                </div>
              )}
              MenuProps={MenuProps}
            >
              {ANNUAL_INCOME_RANGE.map(name => (
                <MenuItem key={name} value={name} style={getStyles(name, formData.targeting.salary_range, theme)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="employment-status-field">Employment Status</InputLabel>
            <Select
              labelId="employment-status-field"
              id="employment-status-field"
              multiple
              value={formData.targeting.employment_status}
              onChange={fieldChange('employment_status', true)}
              input={<Input id="select-multiple-chip" />}
              renderValue={selected => (
                <div className={classes.chips}>
                  {selected.map(value => (
                    <Chip key={value} label={value} className={classes.chip} />
                  ))}
                </div>
              )}
              MenuProps={MenuProps}
            >
              {EMPLOYMENT_STATUS.map(name => (
                <MenuItem key={name} value={name} style={getStyles(name, formData.targeting.employment_status, theme)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="relationship-field">Homeowners?</InputLabel>
            <Select
              labelId="have_houses-field"
              id="have_houses-field"
              value={formData.targeting.have_house}
              onChange={fieldChange('have_house', true)}
            >
              {YES_NO_ALL.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="relationship-field">Pet Owners?</InputLabel>
            <Select
              labelId="pet-owners-field"
              id="pet-owners-field"
              value={formData.targeting.have_pet}
              onChange={fieldChange('have_pet', true)}
            >
              {PET_OWNERS_STATUS.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        <FormControl className={classes.formControl}>
          <InputLabel id="customer-list-select-field">Select Customer List</InputLabel>
          <Select
            labelId="customer-list-select-field"
            id="customer-list-select"
            value={formData.customer_list_id}
            onChange={fieldChange('customer_list_id', false)}
          >
            {customerLists.map(customerList => (
              <MenuItem key={customerList.customer_list_id} value={customerList.customer_list_id}>
                {customerList.customer_list_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      </div>
      <div style={{ flex: 1, alignItems: 'center' }}>
        <CustomText
          text="Total Count:"
          style="totalCount"
          align="center"
        />
        <CustomText style="totalNumber">{audienceCount}</CustomText>
      </div>
    </div>
  );
};

export default WizardStep1;
